/**
 * NotificationBell for the Supplier portal.
 *
 * Uses the supplier-session JWT and supplier-portal notification endpoints.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Bell, Info, Check, Loader2, UserPlus, FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supplierNotificationAPI } from "../services/supplierApi";
import { useSupplierAuth } from "../context/SupplierAuthContext";
import { timeAgo } from "../../utils/timeAgo";
import { useTimeRefresh } from "../../hooks/useTimeRefresh";

const CAT_META = {
  PROCUREMENT:  { Icon: FileText, color: "text-secondary", bg: "bg-surface-sunken" },
  SUPPLIER_OPS: { Icon: UserPlus, color: "text-secondary", bg: "bg-surface-sunken" },
  SYSTEM:       { Icon: Info, color: "text-secondary", bg: "bg-surface-sunken" },
};

export default function SupplierNotificationBell() {
  const { isAuthenticated } = useSupplierAuth();
  const navigate = useNavigate();
  useTimeRefresh(30_000);

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const mountedRef = useRef(true);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 8 });

  const updatePos = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPanelPos({ top: Math.max(8, r.bottom + 8), right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!anchorRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key !== "Escape" || !open) return;
      setOpen(false);
      window.requestAnimationFrame(() => anchorRef.current?.querySelector("button")?.focus());
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const h = () => updatePos();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
  }, [open, updatePos]);

  useEffect(() => {
    if (!isAuthenticated) { setCount(0); setItems([]); return; }
    supplierNotificationAPI.unreadCount().then((r) => mountedRef.current && setCount(r?.count ?? 0)).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => { supplierNotificationAPI.unreadCount().then((r) => mountedRef.current && setCount(r?.count ?? 0)).catch(() => {}); };
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(refresh, 30_000);
    return () => { document.removeEventListener("visibilitychange", onVis); clearInterval(interval); };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!open || !isAuthenticated) return;
    setLoading(true);
    supplierNotificationAPI.list({ page: 1, limit: 6 })
      .then((res) => { if (mountedRef.current) setItems(res?.data || res?.items || []); })
      .catch(() => {})
      .finally(() => mountedRef.current && setLoading(false));
  }, [open, isAuthenticated]);

  const handleMarkRead = async (e, id) => {
    e.stopPropagation();
    try { await supplierNotificationAPI.markRead(id); setItems((p) => p.map((n) => (n.id === id ? { ...n, is_read: true } : n))); setCount((p) => Math.max(0, p - 1)); } catch {}
  };

  const handleItemClick = (n) => {
    if (!n.is_read) { supplierNotificationAPI.markRead(n.id).catch(() => {}); setItems((p) => p.map((x) => (x.id === n.id ? { ...x, is_read: true } : x))); setCount((p) => Math.max(0, p - 1)); }
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
    else navigate("/supplier/notifications");
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative z-[120]" ref={anchorRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-control text-secondary transition-colors hover:bg-hover hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus max-md:h-11 max-md:w-11"
        aria-label={count > 0 ? `${count} unread notifications` : "Notifications"}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-counter tabular-nums text-on-accent ring-2 ring-surface">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: `${panelPos.top}px`, right: `${panelPos.right}px`, zIndex: 2000 }}
          className="fixed w-[calc(100vw-1rem)] max-w-sm overflow-hidden rounded-panel border border-default bg-surface-raised text-foreground shadow-menu sm:w-96"
        >
          <div className="flex items-center justify-between border-b border-subtle px-4 py-3">
            <span className="text-panel-title text-foreground">Notifications</span>
          </div>

          <div className="max-h-[min(360px,calc(100dvh-8rem))] overflow-y-auto scrollbar-fade">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-tertiary" /></div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={20} className="mx-auto mb-2 text-tertiary" />
                <p className="text-body-sm text-secondary">No notifications yet</p>
              </div>
            ) : (
              items.map((n) => {
                const cat = CAT_META[n.category] || CAT_META.SYSTEM;
                const isRead = n.is_read || n.isRead;
                return (
                  <div
                    key={n.id}
                    className={["group relative border-b border-subtle last:border-b-0",
                      isRead ? "hover:bg-hover" : "bg-accent-subtle/40 hover:bg-hover"
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      onClick={() => handleItemClick(n)}
                      className="flex w-full cursor-pointer gap-3 px-4 py-3 pr-10 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus"
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cat.bg}`}>
                        <cat.Icon size={14} className={cat.color} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-2">
                          <span className={["truncate text-body-sm font-medium", isRead ? "text-secondary" : "text-foreground"].join(" ")}>{n.title}</span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            <span className="whitespace-nowrap text-meta text-tertiary">{timeAgo(n.created_at || n.createdAt)}</span>
                            {!isRead && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />}
                          </span>
                        </span>
                        <span className="mt-0.5 block line-clamp-1 text-meta text-secondary">{n.body}</span>
                      </span>
                    </button>
                    {!isRead && (
                      <button type="button" onClick={(e) => handleMarkRead(e, n.id)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-control p-1 opacity-100 transition-opacity hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" title="Mark as read">
                        <Check size={12} className="text-secondary" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <button
            onClick={() => { setOpen(false); navigate("/supplier/notifications"); }}
            className="block w-full border-t border-subtle py-2.5 text-center text-body-sm font-medium text-accent transition-colors hover:bg-hover"
          >
            See all notifications
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
